import { useEffect, useState } from 'react';
import { useFinancialEntitiesContext } from '../financialEntities';

export const TRANSACTION_SYNCING_TEXT = {
    success: {
        title: 'Your budget is ready!',
        subtitle:
            'We looked at your last 3 months of spending to suggest a starting budget.',
    },
    loading: {
        title: 'Analyzing recent spending',
        subtitle: `We’re analyzing your transactions to recommend a budget for you.
             
This may take up to five minutes.

If you don’t want to wait, we’ll message once we’re done.
`,
    },
    timeout: {
        title: 'Still crunching the numbers',
        subtitle: `This is taking a little longer than usual. 
                    
You can start from scratch now, or hang tight while we finish setting up smart budgets for you.`,
    },
};

export const useAreTransactionsSynced = () => {
    const { fes } = useFinancialEntitiesContext();

    if (!fes.unfilteredData) return false;

    const transactionSyncStatuses = fes.unfilteredData.financialEntities
        .filter(
            (fe) => fe.type === 'CheckingAccount' || fe.type === 'CreditCard'
        )
        .map((fe) => fe.transactionSyncStatus);

    return transactionSyncStatuses.every(
        (status) =>
            status === 'INITIAL_SYNCED' ||
            status === 'UPDATE_SYNCED' ||
            status === 'UPDATE_SYNC_FAILED' ||
            status === 'UPDATE_CATEGORIZING' ||
            status === 'UPDATE_FETCHING_FROM_PROVIDER'
    );
};

type TransactionSyncState = 'success' | 'loading' | 'timeout';

export const useTransactionsSyncState = (): TransactionSyncState => {
    const areTransactionsSynced = useAreTransactionsSynced();
    const [isTimeoutComplete, setIsTimeoutComplete] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsTimeoutComplete(true);
        }, 10000);

        return () => clearTimeout(timer);
    }, []);

    return areTransactionsSynced
        ? 'success'
        : isTimeoutComplete
          ? 'timeout'
          : 'loading';
};
